import React, { useMemo, useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, Save, X } from 'lucide-react';
import { useExpenseCategories } from '@/hooks/finance/useExpenseCategories';
import { useDepartmentCostPositions } from '@/hooks/finance/useDepartmentCostPositions';
import {
  useAllocationsByCategory,
  useSaveCostPositionAllocations,
} from '@/hooks/finance/useCostPositionAllocations';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BUAllocationEditor,
  AllocationValue,
  allocationsAreValid,
} from '@/components/finance/BUAllocationEditor';

const BusinessUnitAssignmentsPage: React.FC = () => {
  const { data: categories, isLoading } = useExpenseCategories();
  const { data: deptMappings } = useDepartmentCostPositions();
  const { allocationsByCategory, isLoading: loadingAllocs } = useAllocationsByCategory();
  const saveAllocations = useSaveCostPositionAllocations();

  const deptsByPositionId = useMemo(() => {
    const map = new Map<string, string[]>();
    (deptMappings || []).forEach((m: any) => {
      const name = m.department?.name;
      if (!name || !m.category_id) return;
      const arr = map.get(m.category_id) || [];
      arr.push(name);
      map.set(m.category_id, arr);
    });
    return map;
  }, [deptMappings]);

  const [search, setSearch] = useState('');
  const [filterBU, setFilterBU] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<AllocationValue[]>([]);

  const positions = useMemo(() => {
    if (!categories) return [];
    const groupById = new Map(categories.map((c) => [c.id, c]));
    return categories
      .filter((c) => c.category_type === 'cost_position')
      .map((p) => {
        const group = p.parent_id ? groupById.get(p.parent_id) : undefined;
        const top = group?.parent_id ? groupById.get(group.parent_id) : undefined;
        return {
          ...p,
          group_name: group?.name ?? '—',
          top_name: top?.name ?? '—',
        };
      })
      .sort(
        (a, b) =>
          a.top_name.localeCompare(b.top_name) ||
          a.group_name.localeCompare(b.group_name) ||
          a.name.localeCompare(b.name)
      );
  }, [categories]);

  const getAllocs = (id: string): AllocationValue[] =>
    (allocationsByCategory.get(id) || []).map((a) => ({
      business_unit: a.business_unit,
      percentage: Number(a.percentage),
    }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return positions.filter((p) => {
      const allocs = getAllocs(p.id);
      const isUnassigned = allocs.length === 0;
      const isSplit = allocs.length > 1;
      const buMatch = (bu: string) => allocs.some((a) => a.business_unit === bu);

      if (filterBU === 'unassigned' && !isUnassigned) return false;
      if (filterBU === 'split' && !isSplit) return false;
      if (
        filterBU !== 'all' &&
        filterBU !== 'unassigned' &&
        filterBU !== 'split' &&
        !buMatch(filterBU)
      )
        return false;

      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        p.group_name.toLowerCase().includes(q) ||
        p.top_name.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, search, filterBU, allocationsByCategory]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: positions.length, unassigned: 0, split: 0 };
    BUSINESS_UNITS.forEach((b) => (c[b.value] = 0));
    positions.forEach((p) => {
      const allocs = getAllocs(p.id);
      if (allocs.length === 0) c.unassigned += 1;
      if (allocs.length > 1) c.split += 1;
      allocs.forEach((a) => (c[a.business_unit] = (c[a.business_unit] || 0) + 1));
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, allocationsByCategory]);

  const openEdit = (id: string) => {
    setEditingId(id);
    setEditingValue(getAllocs(id));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue([]);
  };

  const handleSave = async () => {
    if (!editingId) return;
    if (!allocationsAreValid(editingValue)) {
      toast.error('Allocations must sum to 100% (or be empty).');
      return;
    }
    try {
      await saveAllocations.mutateAsync({
        category_id: editingId,
        allocations: editingValue.filter((v) => v.percentage > 0),
      });
      toast.success('Allocations updated');
      setEditingId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    }
  };

  const renderAllocBadges = (allocs: AllocationValue[]) => {
    if (allocs.length === 0) {
      return <span className="text-muted-foreground text-xs">Unassigned</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {allocs.map((a) => {
          const bu = BUSINESS_UNITS.find((b) => b.value === a.business_unit);
          return (
            <Badge
              key={a.business_unit}
              variant="outline"
              style={{ borderColor: bu?.color, color: bu?.color }}
            >
              {bu?.label} {a.percentage}%
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Business Unit Assignments</h1>
          <p className="text-muted-foreground">
            Split each cost position across business units by percentage (e.g. 50% ChipChip, 30%
            Zeami, 20% Grantt). Expenses with no explicit BU will be allocated automatically in the
            Business Unit P&amp;L.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Cost Positions</CardTitle>
            <CardDescription>
              {filtered.length} of {positions.length} cost positions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name, code, group, or category…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <Tabs value={filterBU} onValueChange={setFilterBU}>
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="unassigned">Unassigned ({counts.unassigned})</TabsTrigger>
                <TabsTrigger value="split">Split ({counts.split})</TabsTrigger>
                {BUSINESS_UNITS.map((b) => (
                  <TabsTrigger key={b.value} value={b.value}>
                    {b.label} ({counts[b.value] || 0})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {isLoading || loadingAllocs ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cost Position</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Cost Group</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Department(s)</TableHead>
                      <TableHead>BU Allocation</TableHead>
                      <TableHead className="w-[140px] text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => {
                      const allocs = getAllocs(p.id);
                      const isEditing = editingId === p.id;
                      return (
                        <React.Fragment key={p.id}>
                          <TableRow>
                            <TableCell className="font-medium">{p.name}</TableCell>
                            <TableCell className="text-muted-foreground text-xs">{p.code}</TableCell>
                            <TableCell>{p.group_name}</TableCell>
                            <TableCell>{p.top_name}</TableCell>
                            <TableCell>
                              {(() => {
                                const depts = deptsByPositionId.get(p.id) || [];
                                if ((p as any).is_global) {
                                  return <Badge variant="secondary">Global</Badge>;
                                }
                                if (depts.length === 0) {
                                  return <span className="text-muted-foreground text-xs">—</span>;
                                }
                                return (
                                  <div className="flex flex-wrap gap-1">
                                    {depts.map((d) => (
                                      <Badge key={d} variant="outline">
                                        {d}
                                      </Badge>
                                    ))}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>{renderAllocBadges(allocs)}</TableCell>
                            <TableCell className="text-right">
                              {isEditing ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={
                                      saveAllocations.isPending ||
                                      !allocationsAreValid(editingValue)
                                    }
                                  >
                                    {saveAllocations.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Save className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEdit(p.id)}
                                >
                                  Edit split
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {isEditing && (
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableCell colSpan={7} className="p-4">
                                <BUAllocationEditor
                                  value={editingValue}
                                  onChange={setEditingValue}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No cost positions match your filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BusinessUnitAssignmentsPage;
